import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axiosClient from '../axios-client';
import { AuthContext } from '../Context/UserContext';
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import video from "../assets/videos/video.mp4";
import { IoIosArrowBack } from "react-icons/io";
import CryptoJS from "crypto-js";
import { decryptId } from '../utils/encryption';

const chunkLabelToLowercase = (label) => {
  switch (label) {
    case "NUDITÉ": return "nudité";
    case "SCÈNE INTIME": return "scène intime";
    case "VIOLENCE PHYSIQUE": return "violence physique";
    case "VULGARITÉ SONORE": return "vulgarité sonore";
    case "VULGARITÉ VISUELLE": return "vulgarité visuelle";
    default: return label.toLowerCase();
  }
};

function findOverlappingEnd(chunks, currentTime, filters) {
  // Filter the chunks based on the user's filters
  let filteredChunks = chunks.filter(chunk =>
    (chunk.label === "VULGARITÉ SONORE" || chunk.label === "VULGARITÉ VISUELLE") &&
    chunk.start <= currentTime && chunk.end > currentTime &&
    filters[chunk.label]  // Only consider chunks that are active in the filters
  );

  if (filteredChunks.length < 2) return null;

  let soundChunk = filteredChunks.find(chunk => chunk.label === "VULGARITÉ SONORE");
  let visualChunk = filteredChunks.find(chunk => chunk.label === "VULGARITÉ VISUELLE");

  if (!soundChunk || !visualChunk) return null;

  return Math.min(soundChunk.end, visualChunk.end);
}

function PlayVideo() {
  const { id } = useParams();
  const [contentDetails, setContentDetails] = useState();
  const location = useLocation();
  const [videoChunks, setVideoChunks] = useState([]);
  const { profileId } = useContext(AuthContext);
  const [filters, setFilters] = useState({});
  const [videoStyle, setVideoStyle] = useState({});
  const videoRef = useRef(null);
  const lastToastRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [watchHistoryId, setWatchHistoryId] = useState(null);
  const [whIsFinished, setWhIsFinished] = useState(false);
  const [whFrequency, setWhFrequency] = useState(1);
  const [progress, setProgress] = useState(0);
  const [isProgressSet, setIsProgressSet] = useState(false);
  const navigate = useNavigate()
  // const secretKeyEnv = process.env.REACT_APP_SECRET_KEY;

  //  const decryptId = (encryptedId) => {
  //     try {
  //       const key = secretKeyEnv;
  //       const decoded = atob(encryptedId);
  //       const decryptedBytes = CryptoJS.AES.decrypt(decoded, key);
  //       return decryptedBytes.toString(CryptoJS.enc.Utf8);
  //     } catch (error) {
  //       // console.error("Error decrypting token:", error);
  //       return null;
  //     }
  //   };  
  useEffect(() => {
    // Ensure the video starts from the correct progress position
    if (videoRef.current && progress > 0) {
      videoRef.current.currentTime = progress;
      setIsLoaded(true);
    }
  }, [progress]);

  useEffect(() => {

    if (videoRef.current) {
      videoRef.current.preload = "auto";
      setIsLoaded(true);
    }
  }, [contentDetails?.videoUrl]);

  const fetchContentDetails = async (id) => {
    try {
      let response;
      if (location.state.type === "episode") {
        response = await axiosClient.get(`/episodes/${id}/`);
      } else if (location.state.type === "movie") {
        response = await axiosClient.get(`/movies/${id}/`);
      }
      const contentData = response.data;

      setContentDetails(contentData);
      if (contentData.videoUrl) {
        setIsLoaded(false);
      }
      // Check if a watch history already exists
      const watchHistory = await fetchExistingWatchHistory(contentData);

      if (watchHistory) {
        setWatchHistoryId(watchHistory.id);
        setWhIsFinished(watchHistory.isFinished);
        setWhFrequency(watchHistory.frequency);
        setProgress(watchHistory.progress);
        setIsProgressSet(false);
        // Ensure the video reflects the fetched progress
        videoRef.current.currentTime = watchHistory.progress;

      } else {

        await registerWatchHistory(contentData);
      }



      setVideoChunks(contentData.filter_chunks || []);
    } catch (error) {
      // console.error("Error fetching content details:", error);
    }
  };

  const handleCanPlay = () => {
    if (!isProgressSet && progress > 0 && videoRef.current) {
      videoRef.current.currentTime = progress;
      setIsProgressSet(true);
    }
    setIsLoaded(true);

  };

  const fetchExistingWatchHistory = async (content) => {
    try {

      const response = await axiosClient.get("watch-history/check/", {
        params: {
          profile: profileId,
          movie: location.state.type === "movie" ? content.id : null,
          episode: location.state.type === "episode" ? content.id : null,
        },
      });

      // Check the response to see if watch history exists
      if (response.data.exists) {
        return response.data.data; // If exists, return the data
      } else {
        return null; // If not, return null
      }
    } catch (error) {
      // console.error("Error fetching existing watch history:", error);
      return null;
    }
  };


  const registerWatchHistory = async (content) => {
    try {
      const response = await axiosClient.post("watch-history/create/", {
        profile: profileId,
        movie: location.state.type === "movie" ? content.id : null,
        episode: location.state.type === "episode" ? content.id : null,
        progress: 0,
      });

      // Handle both new and existing records
      setWatchHistoryId(response.data.id);
      setWhIsFinished(response.data.isFinished);
      setWhFrequency(response.data.frequency);
    } catch (error) {
      // console.error("Error registering watch history:", error);
    }
  };

  const updateWatchHistory = async () => {
    if (!watchHistoryId || !videoRef.current) return;

    try {
      const progress = (videoRef.current.currentTime);
      const lastWatchedOn = new Date().toISOString()
      const isFinished = false
      let frequency = whFrequency
      if (whIsFinished) {
        frequency = frequency + 1
      }
      const data = {
        progress,
        frequency,
        isFinished,
        lastWatchedOn
      };

      // Use the id from useParams and location.state.type to determine the correct field to update
      if (location.state.type === "movie") {
        data.movie = decryptId(id);  // If it's a movie, add the movie ID
      } else if (location.state.type === "episode") {
        data.episode = decryptId(id);  // If it's an episode, add the episode ID
      }

      // Make the patch request with the data
      await axiosClient.patch(`watch-history/update/${watchHistoryId}/`, data);
    } catch (error) {
      console.error("Error updating watch history:", error);
    }
  };

  const fetchProfileFilters = async (id) => {
    try {
      const response = await axiosClient.get(`/profiles/${id}/`);

      setFilters(response.data.filters || {});
    } catch (error) {
      // console.error("Error fetching profile filters:", error);
    }
  };

  useEffect(() => {
    fetchProfileFilters(profileId);
    fetchContentDetails(decryptId(id));
  }, [id, profileId]);

  const [lastUpdateTime, setLastUpdateTime] = useState(0);
  const deleteWatchHistory = async () => {
    if (!watchHistoryId) return;

    try {
      const isFinished = true
      const data = {
        progress,
        isFinished
      };

      // Use the id from useParams and location.state.type to determine the correct field to update
      if (location.state.type === "movie") {
        data.movie = decryptId(id);  // If it's a movie, add the movie ID
      } else if (location.state.type === "episode") {
        data.episode = decryptId(id);  // If it's an episode, add the episode ID
      }

      // Make the patch request with the data
      await axiosClient.patch(`watch-history/update/${watchHistoryId}/`, data);
    } catch (error) {
      // console.error("Error updating watch history:", error);
    }
  };
  const handleTimeUpdate = async () => {
    if (!isLoaded) return;
    const currentTime = videoRef.current.currentTime;


    if (contentDetails.duration - currentTime <= 30) {
      await deleteWatchHistory(); // Supprimer l'historique de visionnage
      // await videoRef.current.pauseAsync(); 
      return; // Sortir de la fonction pour éviter d'autres traitements
    }

    if (Math.abs(currentTime - lastUpdateTime) >= 30) {
      updateWatchHistory();
      setLastUpdateTime(currentTime);
    }
    const activeChunks = videoChunks.filter(
      chunk => currentTime >= chunk.start && currentTime < chunk.end && filters[chunk.label]
    );

    if (activeChunks.length === 0) {
      setVideoStyle({ filter: "none" });
      // videoRef.current.muted = false;
      lastToastRef.current = null;
      return;
    }

    const overlappingEnd = findOverlappingEnd(videoChunks, currentTime, filters);
    if (overlappingEnd) {
      videoRef.current.currentTime = overlappingEnd;
      setVideoStyle({ filter: "none" });
      videoRef.current.muted = false;
      toast.info("La scène a été sautée car elle contient de la vulgarité sonore et visuelle.");
      return;
    }

    const skipTypes = ["NUDITÉ", "SCÈNE INTIME", "VIOLENCE PHYSIQUE"];
    const needsSkip = activeChunks.some(chunk => skipTypes.includes(chunk.label));
    const hasSoundVulgarity = activeChunks.some(chunk => chunk.label === "VULGARITÉ SONORE");
    const hasVisualVulgarity = activeChunks.some(chunk => chunk.label === "VULGARITÉ VISUELLE");

    if (needsSkip) {
      const skipChunk = activeChunks.find(chunk => skipTypes.includes(chunk.label));
      videoRef.current.currentTime = skipChunk.end;
    } else if (hasSoundVulgarity) {
      const soundChunk = activeChunks.find(chunk => chunk.label === "VULGARITÉ SONORE");
      videoRef.current.muted = true;
      setVideoStyle({ filter: "none" });
      console.log(soundChunk);

      if (soundChunk && Math.abs(soundChunk.end - currentTime) < 0.5) {

        videoRef.current.muted = false;
      }
    } else if (hasVisualVulgarity) {
      videoRef.current.muted = false;
      setVideoStyle({ filter: "brightness(0)" });
    }

    if (lastToastRef.current !== activeChunks.map(chunk => chunk.label).join(',')) {
      lastToastRef.current = activeChunks.map(chunk => chunk.label).join(',');
      const lowercaseLabels = activeChunks.map(chunk => chunkLabelToLowercase(chunk.label));
      const message = lowercaseLabels.length > 2
        ? `${lowercaseLabels.slice(0, -1).join(", ")} et ${lowercaseLabels[lowercaseLabels.length - 1]}`
        : lowercaseLabels.join(" et ");

      let toastMessage = "";
      if (needsSkip) {
        toastMessage = `La scène a été sautée car elle contient ${message}.`;
      } else if (hasSoundVulgarity) {
        toastMessage = `La scène a été mise en sourdine car elle contient ${message}.`;
      } else if (hasVisualVulgarity) {
        toastMessage = `La scène a été assombrie car elle contient ${message}.`;
      }
      toast.info(toastMessage);
    }
  };


  const goback = () => {
    console.log('test goback');
    if (location.state.subCatId) {
      navigate(location.state.from, {
        state: {
          subCatId: location.state.subCatId,
        },
      });
    }
    else if (location.state.catId) {
      navigate(location.state.from, {
        state: {
          catId: location.state.catId,
        },
      });
    }
    else if (location.state.season) {
      navigate(location.state.from, {
        state: {
          season: location.state.season,
        },
      });
    }
    else {
      navigate(location.state.from)
    }

  }



  return (
    <div className="h-screen w-screen flex items-center justify-center bg-black relative ">
      <button
        onClick={goback}
        className="z-50 absolute top-5 left-5 p-2.5 bg-gray-700 bg-opacity-80  text-white rounded-full  hover:bg-gray-600 hover:bg-opacity-80 transition"
      >
        <IoIosArrowBack size={25} />
      </button>

      {(!contentDetails?.videoUrl) ? (
        <div className='text-white'>Loading...</div>
      ) : (
        <div >
          <video
            className='h-fit lg:h-screen '
            ref={videoRef}
            // width="100%"
            // height="100%"

            controls
            autoPlay
            onTimeUpdate={handleTimeUpdate}
            style={videoStyle}
            onCanPlay={handleCanPlay}
            controlsList="nodownload"
          // src={contentDetails.videoUrl}
          // />
          >
            <source src={contentDetails.videoUrl} type="video/mp4" />
            {/* <source src={video} type="video/mp4" /> */}
          </video>

        </div>

      )}
      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
}

export default PlayVideo;